// src/pages/admin/Users.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Checkbox,
  TextField,
  InputAdornment,
  Skeleton,
  Chip,
  Drawer,
  Tabs,
  Tab,
  Divider,
  Button,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { Search, Close, Save } from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  fetchUsers,
  fetchUserById,
  updateUser,
  resetPassword,
  fundUser,
} from "../utils/api";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Drawer state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);

  // Profile edit form
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    account: "",
    balance: "",
  });
  const [saving, setSaving] = useState(false);

  // Fund Modal
  const [fundOpen, setFundOpen] = useState(false);
  const [fundValues, setFundValues] = useState({
    amount: "",
    balance_type: "total_balance",
    action: "add",
    description: "",
  });
  const [fundLoading, setFundLoading] = useState(false);

  // Reset Password Modal
  const [resetOpen, setResetOpen] = useState(false);
  const [resetValues, setResetValues] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [resetLoading, setResetLoading] = useState(false);

  // Fetch users
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetchUsers({
        page: page + 1,
        per_page: rowsPerPage,
        search,
      });
      setUsers(res.data || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Failed to load users", severity: "error" },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, search]);

  // Load form when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      setFormValues({
        name: selectedUser.name,
        email: selectedUser.email,
        account: selectedUser.account_number, // ✅ fix
        balance: selectedUser.total_balance,
      });
    }
  }, [selectedUser]);

  const handleSelectAll = (event) => {
    setSelected(event.target.checked ? users.map((u) => u.id) : []);
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openDetails = async (user) => {
    setDetailsOpen(true);
    setSelectedUser(user);
    try {
      const res = await fetchUserById(user.id);
      setSelectedUser(res.data);
    } catch (err) {
      console.error("Failed to fetch user details:", err);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedUser(null);
    setActiveTab(0);
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await updateUser(selectedUser.id, {
        ...formValues,
        total_balance: formValues.balance,
        account_number: formValues.account, // ✅ fix
      });
      await loadUsers();
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: {
            message: "Profile updated successfully",
            severity: "success",
          },
        })
      );
    } catch (err) {
      console.error("Update failed:", err);
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Update failed", severity: "error" },
        })
      );
    } finally {
      setSaving(false);
    }
  };

  // Reset Password handlers
  const openResetModal = (user) => {
    setSelectedUser(user);
    setResetOpen(true);
  };
  const closeResetModal = () => {
    setResetOpen(false);
    setResetValues({ new_password: "", confirm_password: "" });
  };
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (resetValues.new_password !== resetValues.confirm_password) {
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Passwords do not match", severity: "error" },
        })
      );
      return;
    }
    setResetLoading(true);
    try {
      await resetPassword(selectedUser.id, resetValues);
      closeResetModal();
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Password reset successfully", severity: "success" },
        })
      );
    } catch (err) {
      console.error("Reset failed:", err);
    } finally {
      setResetLoading(false);
    }
  };

  // Fund modal handlers
  const openFundModal = (user) => {
    setSelectedUser(user);
    setFundOpen(true);
  };
  const closeFundModal = () => {
    setFundOpen(false);
    setFundValues({
      amount: "",
      balance_type: "total_balance",
      action: "add",
      description: "",
    });
  };
  const handleFund = async () => {
    if (!selectedUser) return;
    setFundLoading(true);
    try {
      await fundUser(selectedUser.id, fundValues);
      await loadUsers();
      closeFundModal();
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Balance updated", severity: "success" },
        })
      );
    } catch (err) {
      console.error("Funding failed:", err);
    } finally {
      setFundLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: "#0ff", fontWeight: "bold", mb: { xs: 2, sm: 0 } }}
        >
          Users Management
        </Typography>
        <TextField
          size="small"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#aaa" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "rgba(25,25,50,0.7)",
            input: { color: "#fff" },
            borderRadius: 2,
            width: { xs: "100%", sm: "250px" },
          }}
        />
      </Box>

      {/* Table */}
      <Card
        sx={{
          bgcolor: "rgba(25,25,50,0.7)",
          border: "1px solid #0ff4",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.length > 0 && selected.length === users.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>KYC Status</TableCell>
                <TableCell>Account #</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(rowsPerPage)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton
                          variant="rectangular"
                          height={40}
                          sx={{ bgcolor: "rgba(255,255,255,0.1)" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                : users.map((user) => (
                    <TableRow
                      key={user.id}
                      component={motion.tr}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(user.id)}
                          onChange={() => handleSelect(user.id)}
                        />
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.kyc_status}
                          color={
                            user.kyc_status?.toLowerCase() === "approved"
                              ? "success"
                              : user.kyc_status?.toLowerCase() === "pending"
                              ? "warning"
                              : "error"
                          } // ✅ fix
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{user.account_number}</TableCell>
                      <TableCell align="right">
                        ${Number(user.total_balance).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                          <Button variant="outlined" size="small" onClick={() => openDetails(user)}>
                            View
                          </Button>
                          <Button variant="outlined" size="small" onClick={() => openDetails(user)}>
                            Edit
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={() => openResetModal(user)}
                          >
                            Reset Password
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            onClick={() => openFundModal(user)}
                          >
                            Fund
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={detailsOpen}
        onClose={closeDetails}
        disableScrollLock={false}
        transitionDuration={100}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 450 },
            bgcolor: "#1a1a2b",
            color: "#fff",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, color: "#0ff", fontWeight: "bold" }}>
            {selectedUser?.name || "User Details"}
          </Typography>
          <IconButton color="error" onClick={closeDetails}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: "#0ff", height: "3px" } }}
        >
          <Tab label="Profile" />
          <Tab label="Cards" />
          <Tab label="Transactions" />
          <Tab label="KYC" />
        </Tabs>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ p: 2, overflowY: "auto", height: "calc(100% - 120px)" }}>
          {activeTab === 0 && selectedUser && (
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={formValues.name}
                onChange={(e) => handleChange("name", e.target.value)}
                fullWidth
                InputProps={{ style: { color: "#fff" } }}
              />
              <TextField
                label="Email"
                type="email"
                value={formValues.email}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
                InputProps={{ style: { color: "#fff" } }}
              />
              <TextField
                label="Account Number"
                value={formValues.account}
                onChange={(e) => handleChange("account", e.target.value)}
                fullWidth
                InputProps={{ style: { color: "#fff" } }}
              />
              <TextField
                label="Balance"
                type="number"
                value={formValues.balance}
                onChange={(e) => handleChange("balance", e.target.value)}
                fullWidth
                InputProps={{ style: { color: "#fff" } }}
              />
              <Button
                variant="contained"
                startIcon={<Save />}
                disabled={saving}
                onClick={handleSave}
                sx={{
                  mt: 2,
                  bgcolor: "#0ff",
                  color: "#000",
                  fontWeight: "bold",
                  "&:hover": { bgcolor: "#0cc" },
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
              </Button>
            </Stack>
          )}

          {/* ✅ Empty states added */}
          {activeTab === 1 && (
            selectedUser?.cards?.length > 0 ? (
              selectedUser.cards.map((card) => (
                <Typography key={card.id}>{card.card_number}</Typography>
              ))
            ) : (
              <Typography color="text.secondary">No cards found.</Typography>
            )
          )}
          {activeTab === 2 && (
            selectedUser?.transactions?.length > 0 ? (
              selectedUser.transactions.map((tx) => (
                <Typography key={tx.id}>
                  {tx.type} - ${tx.amount}
                </Typography>
              ))
            ) : (
              <Typography color="text.secondary">No transactions found.</Typography>
            )
          )}
          {activeTab === 3 && (
            selectedUser?.kyc ? (
              <Typography>KYC Status: {selectedUser.kyc.status}</Typography>
            ) : (
              <Typography color="text.secondary">No KYC submitted.</Typography>
            )
          )}
        </Box>
      </Drawer>

      {/* Fund Modal */}
      <Dialog
        open={fundOpen}
        onClose={closeFundModal}
        fullWidth
        maxWidth="sm"
        transitionDuration={100}
      >
        <DialogTitle>Fund / Deduct Balance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              value={fundValues.amount}
              onChange={(e) => setFundValues({ ...fundValues, amount: e.target.value })}
              fullWidth
            />
            <TextField
              label="Balance Type"
              select
              value={fundValues.balance_type}
              onChange={(e) => setFundValues({ ...fundValues, balance_type: e.target.value })}
              fullWidth
            >
              <MenuItem value="total_balance">Main Balance</MenuItem>
              <MenuItem value="current_balance">Current Balance</MenuItem>
              <MenuItem value="savings_balance">Savings Balance</MenuItem>
            </TextField>
            <TextField
              label="Action"
              select
              value={fundValues.action}
              onChange={(e) => setFundValues({ ...fundValues, action: e.target.value })}
              fullWidth
            >
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="deduct">Deduct</MenuItem>
            </TextField>
            <TextField
              label="Description"
              value={fundValues.description}
              onChange={(e) => setFundValues({ ...fundValues, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFundModal}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleFund}
            disabled={fundLoading}
          >
            {fundLoading ? <CircularProgress size={18} color="inherit" /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog
        open={resetOpen}
        onClose={closeResetModal}
        fullWidth
        maxWidth="sm"
        transitionDuration={100}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="New Password"
              type="password"
              value={resetValues.new_password}
              onChange={(e) => setResetValues({ ...resetValues, new_password: e.target.value })}
              fullWidth
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={resetValues.confirm_password}
              onChange={(e) =>
                setResetValues({ ...resetValues, confirm_password: e.target.value })
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResetModal}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPassword}
            disabled={resetLoading}
          >
            {resetLoading ? <CircularProgress size={18} color="inherit" /> : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}